import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDownIcon, PencilSquareIcon } from "@heroicons/react/20/solid";
import classNames from "../../../utils/classNames";
import { ISavCustomerName } from "../../../data/useSav";

interface ISavCustomerPickerPillProps {
  value: string | null;
  customers: ISavCustomerName[];
  onChange: (customerId: string | null) => void;
  placeholder?: string;
  includeAllOption?: boolean;
  allLabel?: string;
  disabled?: boolean;
}

export default function SavCustomerPickerPill({
  value,
  customers,
  onChange,
  placeholder,
  includeAllOption = false,
  allLabel,
  disabled = false,
}: ISavCustomerPickerPillProps) {
  const { t } = useTranslation("leads");
  const resolvedPlaceholder = placeholder ?? t("Assign customer");
  const resolvedAllLabel = allLabel ?? t("All Customers");
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  const selected = useMemo(() => {
    if (!value) return null;
    return customers.find((c) => c.id === value) ?? null;
  }, [value, customers]);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!ref.current) return;
      if (!ref.current.contains(e.target as Node)) setOpen(false);
    };

    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        disabled={disabled}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className={classNames(
          "inline-flex items-center gap-2 rounded-full px-3 py-1 text-sm font-medium ring-1",
          "bg-white text-gray-700 ring-gray-200 hover:bg-gray-50",
          disabled && "opacity-60 cursor-not-allowed"
        )}
      >
        <span
          className="h-2.5 w-2.5 rounded-full"
          style={{ backgroundColor: selected?.primaryColour || "#9CA3AF" }}
        />
        <span className="max-w-[180px] truncate">
          {selected?.name || (includeAllOption && !value ? resolvedAllLabel : resolvedPlaceholder)}
        </span>
        <PencilSquareIcon className="h-4 w-4 text-gray-400" />
        <ChevronDownIcon className="h-4 w-4 text-gray-400" />
      </button>

      {open && !disabled && (
        <div className="absolute right-0 mt-2 w-72 rounded-lg border border-gray-200 bg-white shadow-lg z-50">
          <div className="p-2 max-h-72 overflow-auto">
            {includeAllOption && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onChange(null);
                  setOpen(false);
                }}
                className="w-full flex items-center justify-between gap-3 rounded-md px-3 py-2 text-left text-sm hover:bg-gray-50"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span className="h-2.5 w-2.5 rounded-full shrink-0 bg-gray-400" />
                  <span className="truncate">{resolvedAllLabel}</span>
                </div>
                {!value && <span className="text-xs font-medium text-primary-700">{t("Selected")}</span>}
              </button>
            )}

            {customers.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onChange(c.id);
                  setOpen(false);
                }}
                className="w-full flex items-center justify-between gap-3 rounded-md px-3 py-2 text-left text-sm hover:bg-gray-50"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span
                    className="h-2.5 w-2.5 rounded-full shrink-0"
                    style={{ backgroundColor: c.primaryColour || "#9CA3AF" }}
                  />
                  <span className="truncate">{c.name}</span>
                </div>
                {value === c.id && <span className="text-xs font-medium text-primary-700">{t("Selected")}</span>}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
